import { Icon20DocumentOutline, Icon20FolderSimpleOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_REQUIRED_B2B_PASSWORD_PUBLIC } from 'reactApp/appHelpers/configHelpers';
import { isPublicPasswordEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicPassword';
import { isPublicPasswordFileEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicPasswordFile';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { renderPublicPassword } from 'reactApp/components/SharingWindow/blocks/PublicPasswordDialog/PublicPasswordDialog.helpers';
import styles from 'reactApp/components/SharingWindow/blocks/Weblink/SharingWeblink.css';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { DOMAIN_ACCESS_OPTIONS, QA_VALUE, TOOLTIP_OFFSET } from 'reactApp/components/SharingWindow/Sharing.constants';
import { renderSharingNew, sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { type SharingContentProps, DomainAccess } from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingDropdown } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getItemTypeName } from 'reactApp/modules/file/utils';
import { getIntraDomainShare } from 'reactApp/modules/home/home.selectors';
import type { XrayItemType } from 'reactApp/modules/home/home.types';
import { weblinkSetDomainAccess } from 'reactApp/modules/modifying/modifying.actions';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import type { DownloadLimit } from 'reactApp/modules/public/public.types';
import { newSendSearchRadarAnalytics } from 'reactApp/modules/search/search.analytics';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { useDisableSeoTooltip } from 'reactApp/ui/DisableSeoTooltip/hooks/useDisableSeoTooltip';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { SharingTooltipPromo } from 'reactApp/ui/SharingTooltips/SharingTooltipPromo';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

interface Props {
    item: PublishItem;
    storage: EStorageType;
    canShareForDomestic: boolean;
    showSharingTooltipPromo: boolean;
    downloadLimits: DownloadLimit;
    isBizUser?: boolean;
    formProps: SharingContentProps;
    pin?: string;
    onCloseModal: () => void;
    variant?: SHARING_VARIANT;
    xrayItemType: XrayItemType;
}

export const SharingOptionDomain = memo(
    // eslint-disable-next-line max-lines-per-function
    ({
        item,
        storage,
        canShareForDomestic,
        showSharingTooltipPromo,
        downloadLimits,
        isBizUser,
        formProps,
        pin,
        onCloseModal,
        variant,
        xrayItemType,
    }: Props) => {
        const redesign = variant !== SHARING_VARIANT.default;
        const ref = useRef<HTMLDivElement>(null);
        const dispatch = useDispatch();
        const { xPageId, xReqId, query } = useSelector(getSearchRequestParams);
        const isPhone = EnvironmentSelectors.isPhone();
        const domainAccess = ('weblinkDomainAccess' in item && item?.weblinkDomainAccess) || DomainAccess.EXTERNAL;
        const isFolder = item?.isFolder;
        const weblinkSeoEnabled = item?.weblinkSeoEnabled && seoEnable;
        const itemName = getItemTypeName(item);

        const isSupportIntraDomainShare = useSelector(getIntraDomainShare);

        const filteredDomainAccessOptions = useMemo(() => {
            const options: Partial<typeof DOMAIN_ACCESS_OPTIONS> = DOMAIN_ACCESS_OPTIONS;

            if (isSupportIntraDomainShare) {
                delete options.external;
            }

            return options;
        }, [isSupportIntraDomainShare]);

        const accessOption = filteredDomainAccessOptions[domainAccess];

        const { tooltip, handleShowTip } = useDisableSeoTooltip(ref, itemName, weblinkSeoEnabled);

        const renderDomesticDropdown = useCallback(
            (onClick): ReactElement => {
                const list = Object.values(filteredDomainAccessOptions)
                    .filter((item) => canShareForDomestic || item.id !== DomainAccess.DOMESTIC)
                    .map((item) => ({
                        id: item.id,
                        text: item.dropdownText,
                        check: item.id === domainAccess,
                        qaValue: item.id,
                    }));

                return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
            },
            [canShareForDomestic, domainAccess, filteredDomainAccessOptions]
        );

        const handleUpdateDomestic = useCallback(
            (id): void => {
                if (id === domainAccess) {
                    return;
                }

                sendGa('domestic');
                sendPaymentGa({
                    eventCategory: ECategoryGa.public,
                    action: 'edit-access',
                    type_for: id,
                    public_id: item?.weblink,
                });

                if (storage === EStorageType.search) {
                    newSendSearchRadarAnalytics({
                        eventCategory: ECategoryGa.publicSearch,
                        action: 'edit-access',
                        searchParams: {
                            search_phrase: query,
                            page_id: xPageId,
                            req_id: xReqId,
                        },
                        items: [
                            {
                                file_name: 'nameWithoutExt' in item && item.nameWithoutExt,
                                type: item.kind,
                                pos: 'pos' in item && item.pos,
                                file_id: item.id,
                                public_id: ('weblink' in item && item.weblink) || '',
                                placement: 'srchSrc' in item && item.srchSrc,
                            },
                        ],
                    });
                }

                const action = () => {
                    if (
                        isPublicPasswordEnabled &&
                        IS_REQUIRED_B2B_PASSWORD_PUBLIC &&
                        id === DomainAccess.EXTERNAL &&
                        (item?.isFolder || isPublicPasswordFileEnabled) &&
                        !pin
                    ) {
                        renderPublicPassword({
                            redesign,
                            item,
                            pin,
                            onSuccess: () => {
                                dispatch(weblinkSetDomainAccess({ domain: id, itemId: item.id, storage, xrayItemType }));
                                renderSharingNew(formProps);
                            },
                            onDialogClose: () => renderSharingNew(formProps),
                        });
                        onCloseModal();
                    } else {
                        dispatch(weblinkSetDomainAccess({ domain: id, itemId: item.id, storage, xrayItemType }));
                    }
                };

                if (id === DomainAccess.EXTERNAL && isBizUser) {
                    openPopupHelper({
                        popupName: popupNames.NO_WARNING,
                        data: {
                            onActionClick: action,

                            id: 'share-to-external-warning-dialog',
                            title: 'Предоставить доступ внешним пользователям?',
                            description: 'Просматривать файл сможет любой человек,\nу которого есть ссылка',
                            buttonAcceptText: 'Предоставить',
                            buttonRejectText: 'Отменить',

                            hideCheckbox: true,
                            compactButtons: true,
                        },
                    });
                } else {
                    action();
                }
            },
            [dispatch, domainAccess, formProps, isBizUser, item, onCloseModal, pin, query, redesign, storage, xPageId, xReqId]
        );

        return (
            <div
                className={classNames(styles.rightItem, { [styles.rightItem_disabled]: weblinkSeoEnabled })}
                data-qa-item={QA_VALUE.domesticItem}
                onClick={handleShowTip}
            >
                <span className={classNames(styles.rightTitle, { [styles.tabs]: redesign })}>
                    {(variant === SHARING_VARIANT.files || variant === SHARING_VARIANT.filesBiz) && <Icon20DocumentOutline />}
                    {(variant === SHARING_VARIANT.folders || variant === SHARING_VARIANT.foldersBiz) && <Icon20FolderSimpleOutline />}
                    {isFolder ? 'Папка доступна' : 'Файл доступен'}
                </span>
                <div className={styles.dropdown} ref={ref}>
                    <Dropdown
                        disabled={weblinkSeoEnabled}
                        withinDialog
                        theme="octavius"
                        qaValue={accessOption?.id}
                        as={SharingDropdown}
                        value={isPhone ? accessOption?.text : accessOption?.text.toLowerCase()}
                        content={renderDomesticDropdown}
                        onClickItem={handleUpdateDomestic}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.domesticDropdownContent}
                        tooltipOffset={TOOLTIP_OFFSET}
                        animatedTooltip
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                        redesign={redesign}
                        noPageOffset
                    />
                </div>
                {!isFolder && showSharingTooltipPromo && (
                    <SharingTooltipPromo isMobile={isPhone} target={ref} downloadLimits={downloadLimits} />
                )}
                {tooltip}
            </div>
        );
    }
);

SharingOptionDomain.displayName = 'SharingOptionDomain';
